import React, { useState, useEffect, useCallback } from 'react';
import { isOsJunk, isSupported, readEntriesToFiles } from '../utils/fileIntake';

export default function PageDropZone({ children, onFilesDropped }) {
  const [isDragging, setIsDragging] = useState(false);
  const [dragCounter, setDragCounter] = useState(0);

  const handleDragEnter = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    
    // Check if dragging files
    if (e.dataTransfer.items && e.dataTransfer.items.length > 0) {
      const hasFiles = Array.from(e.dataTransfer.items).some(item => item.kind === 'file');
      if (hasFiles) {
        setDragCounter(prev => prev + 1);
        setIsDragging(true);
      }
    }
  }, []);

  const handleDragLeave = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    
    setDragCounter(prev => {
      const newCounter = prev - 1;
      if (newCounter === 0) {
        setIsDragging(false);
      }
      return newCounter;
    });
  }, []);

  const handleDragOver = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
  }, []);

  /**
   * Page-level drop. This runs for a drop anywhere on the page, which is what a
   * person actually does, the BatchUploader's own dropzone only sees a drop that
   * lands precisely on it.
   *
   * Two things this has to get right, both of which it previously got wrong and
   * both of which failed SILENTLY (reported by Richard Photo Lab, 2026-08-08:
   * "when I drag the folder to the upload screen it appears like nothing is
   * happening"):
   *
   *  1. FOLDERS. A dropped directory arrives in `dataTransfer.files` as a single
   *     typeless entry that is not a real file. The tree is only reachable via
   *     DataTransferItem.webkitGetAsEntry(), which MUST be called synchronously , 
   *     the item list is neutered as soon as this handler yields. So entries are
   *     captured first, before any await.
   *
   *  2. FILES WITH NO MIME TYPE. Chrome reports `type: ''` for RAW (.CR2, .NEF,
   *     .ARW, .DNG) and other pro formats. Filtering on
   *     `file.type.startsWith('image/')` therefore discarded exactly the files a
   *     film lab uploads. Acceptance is by extension via fileIntake.isSupported,
   *     the same helper the uploader itself uses.
   */
  const handleDrop = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();

    setIsDragging(false);
    setDragCounter(0);

    const dt = e.dataTransfer;
    if (!dt) return;

    // Capture entries SYNCHRONOUSLY, valid only during this event.
    let entries = [];
    try {
      entries = Array.from(dt.items || [])
        .map((it) => (typeof it.webkitGetAsEntry === 'function' ? it.webkitGetAsEntry() : null))
        .filter(Boolean);
    } catch { entries = []; }

    const flatFiles = dt.files ? Array.from(dt.files) : [];
    const accept = (list) => list.filter((f) => f && !isOsJunk(f) && isSupported(f));

    const hand = (files) => {
      const usable = accept(files);
      if (usable.length > 0 && onFilesDropped) onFilesDropped(usable);
    };

    if (entries.some((en) => en.isDirectory)) {
      // Async only AFTER the entries are safely captured.
      readEntriesToFiles(entries)
        .then(hand)
        .catch((err) => {
          console.warn('[drop] folder read failed, falling back to flat files:', err?.message);
          hand(flatFiles);
        });
      return;
    }

    hand(flatFiles);
  }, [onFilesDropped]);

  useEffect(() => {
    // Add event listeners to document to catch all drag events
    document.addEventListener('dragenter', handleDragEnter);
    document.addEventListener('dragleave', handleDragLeave);
    document.addEventListener('dragover', handleDragOver);
    document.addEventListener('drop', handleDrop);
    
    return () => {
      document.removeEventListener('dragenter', handleDragEnter);
      document.removeEventListener('dragleave', handleDragLeave);
      document.removeEventListener('dragover', handleDragOver);
      document.removeEventListener('drop', handleDrop);
    };
  }, [handleDragEnter, handleDragLeave, handleDragOver, handleDrop]);

  return (
    <>
      {children}
      {isDragging && (
        <div className="page-drop-overlay active">
          <div className="page-drop-content">
            <svg width="80" height="80" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
              <polyline points="17 8 12 3 7 8" />
              <line x1="12" y1="3" x2="12" y2="15" />
            </svg>
            <h2>Drop Photos Here</h2>
            <p>Release to upload your images</p>
          </div>
        </div>
      )}
    </>
  );
}